import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Edit, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { VehicleFormModal } from "./vehicle-form-modal";

interface SecurityVehicle {
  id: string;
  securityId: string;
  vehicleType: string;
  registrationNumber: string | null;
  registrationState: string | null;
  vin: string | null;
  engineNumber: string | null;
  hullIdentificationNumber: string | null;
  make: string | null;
  model: string | null;
  variant: string | null;
  yearManufactured: number | null;
  bodyType: string | null;
  colour: string | null;
  odometerKm: number | null;
  engineSizeCc: number | null;
  transmission: string | null;
  fuelType: string | null;
  validationSource: string | null;
  validationDate: string | null;
  validationReference: string | null;
  lengthMetres: string | null;
  beamMetres: string | null;
  createdAt: string;
  updatedAt: string;
}

interface VehicleDetailTabProps {
  securityId: string;
}

const VEHICLE_TYPE_LABELS: Record<string, string> = {
  motor_vehicle: "Motor Vehicle",
  motorcycle: "Motorcycle",
  caravan: "Caravan",
  marine_boat: "Marine Boat",
  marine_jetski: "Marine Jet Ski",
  trailer: "Trailer",
  other_vehicle: "Other Vehicle",
};

const AUSTRALIAN_STATE_LABELS: Record<string, string> = {
  NSW: "NSW",
  VIC: "VIC",
  QLD: "QLD",
  SA: "SA",
  WA: "WA",
  TAS: "TAS",
  NT: "NT",
  ACT: "ACT",
};

function formatYear(value: number | null | undefined): string {
  if (!value) return "-";
  return value.toString();
}

function formatNumber(value: number | null | undefined, suffix?: string): string {
  if (value === null || value === undefined) return "-";
  const formatted = value.toLocaleString("en-AU");
  return suffix ? `${formatted} ${suffix}` : formatted;
}

function formatDecimal(value: string | null | undefined, suffix?: string): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return suffix ? `${num} ${suffix}` : num.toString();
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getVehicleTypeLabel(type: string): string {
  return VEHICLE_TYPE_LABELS[type] || type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function isMarineType(vehicleType: string): boolean {
  return vehicleType === "marine_boat" || vehicleType === "marine_jetski";
}

export function VehicleDetailTab({ securityId }: VehicleDetailTabProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const { data: vehicle, isLoading } = useQuery<SecurityVehicle | null>({
    queryKey: ["/api/crm/securities", securityId, "vehicle"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/vehicle`);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch vehicle details");
      }
      return response.json();
    },
    enabled: !!securityId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!vehicle) {
    return (
      <>
        <Card data-testid="vehicle-detail-tab">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Vehicle-Specific Information</h3>
            <div className="text-center py-12">
              <p className="text-muted-foreground text-sm mb-4">
                No vehicle details recorded yet
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setModalOpen(true)}
                data-testid="vehicle-add-button"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Vehicle Details
              </Button>
            </div>
          </CardContent>
        </Card>

        <VehicleFormModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          securityId={securityId}
        />
      </>
    );
  }

  // Data display card
  return (
    <>
      <Card data-testid="vehicle-detail-tab">
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Vehicle-Specific Information</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModalOpen(true)}
              data-testid="vehicle-edit-button"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit Vehicle Details
            </Button>
          </div>

          {/* Vehicle Type Badge */}
          <div className="mb-4">
            <Badge variant="secondary" className="text-sm">
              {getVehicleTypeLabel(vehicle.vehicleType)}
            </Badge>
          </div>

          {/* Vehicle Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Identity */}
            <div>
              <span className="text-sm text-muted-foreground">Make</span>
              <p className="text-sm font-medium">{vehicle.make || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Model</span>
              <p className="text-sm font-medium">{vehicle.model || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Variant</span>
              <p className="text-sm font-medium">{vehicle.variant || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Year</span>
              <p className="text-sm font-medium">{formatYear(vehicle.yearManufactured)}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Colour</span>
              <p className="text-sm font-medium">{vehicle.colour || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Body Type</span>
              <p className="text-sm font-medium">{vehicle.bodyType || "-"}</p>
            </div>

            {/* Registration */}
            <div>
              <span className="text-sm text-muted-foreground">Registration</span>
              <p className="text-sm font-medium">{vehicle.registrationNumber || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Reg. State</span>
              <p className="text-sm font-medium">
                {vehicle.registrationState
                  ? AUSTRALIAN_STATE_LABELS[vehicle.registrationState] || vehicle.registrationState
                  : "-"}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">VIN</span>
              <p className="text-sm font-medium">{vehicle.vin || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Engine Number</span>
              <p className="text-sm font-medium">{vehicle.engineNumber || "-"}</p>
            </div>

            {/* Specs */}
            <div>
              <span className="text-sm text-muted-foreground">Odometer</span>
              <p className="text-sm font-medium">{formatNumber(vehicle.odometerKm, "km")}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Engine Size</span>
              <p className="text-sm font-medium">{formatNumber(vehicle.engineSizeCc, "cc")}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Transmission</span>
              <p className="text-sm font-medium">{vehicle.transmission || "-"}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Fuel Type</span>
              <p className="text-sm font-medium">{vehicle.fuelType || "-"}</p>
            </div>

            {/* Marine-specific (conditional) */}
            {isMarineType(vehicle.vehicleType) && (
              <>
                <div>
                  <span className="text-sm text-muted-foreground">Hull ID Number</span>
                  <p className="text-sm font-medium">{vehicle.hullIdentificationNumber || "-"}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Length</span>
                  <p className="text-sm font-medium">{formatDecimal(vehicle.lengthMetres, "m")}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Beam</span>
                  <p className="text-sm font-medium">{formatDecimal(vehicle.beamMetres, "m")}</p>
                </div>
              </>
            )}
          </div>

          {/* Validation Section */}
          {(vehicle.validationSource || vehicle.validationDate || vehicle.validationReference) && (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Validation</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">Source</span>
                  <p className="text-sm font-medium">{vehicle.validationSource || "-"}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Date</span>
                  <p className="text-sm font-medium">{formatDate(vehicle.validationDate)}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Reference</span>
                  <p className="text-sm font-medium">{vehicle.validationReference || "-"}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <VehicleFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        securityId={securityId}
        existingVehicle={vehicle}
      />
    </>
  );
}
